import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import Alert from '../components/Alert';
import Icon from '../components/Icon';
import { useProducts } from '../product/ProductContext';
import { formatCount, formatLocalDate } from '../product/productUtils';
import { loadReportEntries, saveReportEntries } from '../utils/reportEntries';
import './reports.css';

const PLATFORMS = [
    { id: 'youtube', label: 'YouTube' },
    { id: 'tiktok', label: 'TikTok' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'x', label: 'X' },
    { id: 'linkedin', label: 'LinkedIn' },
];

const PLATFORM_LABELS = PLATFORMS.reduce((acc, p) => ({ ...acc, [p.id]: p.label }), {});
const platformLabel = (id) => PLATFORM_LABELS[id] || id || 'Other';

const VIDEO_TYPES = [
    { id: 'tutorial', label: 'Tutorial' },
    { id: 'vlog', label: 'Vlog' },
    { id: 'review', label: 'Review' },
    { id: 'shorts', label: 'Shorts' },
    { id: 'educational', label: 'Educational' },
];

const RANGES = [
    { id: 'week', label: 'Week', current: 'This week', previous: 'last week' },
    { id: 'month', label: 'Month', current: 'This month', previous: 'last month' },
    { id: '3months', label: '3 months', current: 'Last 3 months', previous: 'the 3 months before' },
];

const METRICS = [
    { id: 'views', label: 'Views' },
    { id: 'likes', label: 'Likes' },
];

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

// Views / likes may be '' while a cell is being edited, or junk from old storage
const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

// Keep '' so a field can be cleared and retyped instead of snapping back to 0
const parseNumberInput = (value) => (value === '' ? '' : Math.max(0, parseInt(value, 10) || 0));

const percent = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);
const formatPercent = (value) => (value == null ? '—' : `${value.toFixed(1)}%`);

const percentChange = (current, previous) => (previous ? Math.round(((current - previous) / previous) * 100) : null);
const pointChange = (current, previous) => (current == null || previous == null ? null : Math.round((current - previous) * 10) / 10);

// Parse 'YYYY-MM-DD' as a local date (new Date('YYYY-MM-DD') is UTC and can shift the day); null when malformed
const parseLocalDate = (dateStr) => {
    if (typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatDay = (dateStr, options = { month: 'short', day: 'numeric' }) => {
    const date = parseLocalDate(dateStr);
    return date ? date.toLocaleDateString('en-US', options) : '';
};

// Calendar periods (weeks start on Monday). offset -1 is the period before, for the deltas.
const getRangeBounds = (range, offset = 0) => {
    const now = new Date();
    if (range === 'week') {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
        return { start, end: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6) };
    }
    const months = range === '3months' ? 3 : 1;
    const firstMonth = now.getMonth() - (months - 1) + offset * months;
    return {
        start: new Date(now.getFullYear(), firstMonth, 1),
        end: new Date(now.getFullYear(), firstMonth + months, 0),
    };
};

const inRange = (dateStr, bounds) => {
    const date = parseLocalDate(dateStr);
    return Boolean(date) && date >= bounds.start && date <= bounds.end;
};

const getDays = (bounds) => {
    const days = [];
    const current = new Date(bounds.start);
    while (current <= bounds.end) {
        days.push(formatLocalDate(current));
        current.setDate(current.getDate() + 1);
    }
    return days;
};

// Daily bars for a week or month, weekly bars (from Monday) for 3 months
const getChartBins = (range, bounds) => {
    const days = getDays(bounds);
    if (range !== '3months') {
        return days.map(day => ({
            days: [day],
            label: range === 'week' ? formatDay(day, { weekday: 'short' }) : formatDay(day),
            title: formatDay(day, { weekday: 'short', month: 'short', day: 'numeric' }),
        }));
    }
    const bins = [];
    days.forEach((day, index) => {
        if (index === 0 || parseLocalDate(day).getDay() === 1) bins.push({ days: [] });
        bins[bins.length - 1].days.push(day);
    });
    return bins.map(bin => ({
        ...bin,
        label: formatDay(bin.days[0]),
        title: `${formatDay(bin.days[0])} – ${formatDay(bin.days[bin.days.length - 1])}`,
    }));
};

// A round number just above the peak, so the axis reads 0 / half / top
const niceCeil = (value) => {
    if (value <= 2) return 2;
    const magnitude = 10 ** Math.floor(Math.log10(value));
    const step = [1, 2, 4, 5, 10].find(f => f * magnitude >= value);
    return step * magnitude;
};

// Stored entries come from this page, the Content Plan and older versions of
// the app; repair anything that would break rendering
const normalizeEntries = (entries) => {
    const seen = new Set();
    return (Array.isArray(entries) ? entries : []).filter(isObject).map((entry, index) => {
        let { id } = entry;
        if ((typeof id !== 'number' && typeof id !== 'string') || seen.has(id)) id = `entry-${Date.now()}-${index}`;
        seen.add(id);
        const text = (value) => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
        return {
            ...entry,
            id,
            platform: text(entry.platform) || 'youtube',
            videoType: text(entry.videoType),
            description: text(entry.description),
            views: Math.max(0, toNumber(entry.views)),
            likes: Math.max(0, toNumber(entry.likes)),
            postDate: text(entry.postDate),
            linkSlug: typeof entry.linkSlug === 'string' && entry.linkSlug ? entry.linkSlug : undefined,
        };
    });
};

// Tracked links from the API; anything without a slug can't be matched to a post
const normalizeLinks = (links) => (Array.isArray(links) ? links : [])
    .filter(link => isObject(link) && typeof link.slug === 'string' && link.slug)
    .map(link => ({
        ...link,
        clicks: toNumber(link.clicks),
        byDay: isObject(link.byDay) ? link.byDay : {},
        campaign: typeof link.campaign === 'string' ? link.campaign : '',
    }));

const sumField = (entries, field) => entries.reduce((total, entry) => total + toNumber(entry[field]), 0);

const summarize = (entries, links, linkBySlug, bounds) => {
    const inPeriod = entries.filter(entry => inRange(entry.postDate, bounds));
    const views = sumField(inPeriod, 'views');
    const likes = sumField(inPeriod, 'likes');
    // Click rate only counts posts that have a tracked link
    const linked = inPeriod.filter(entry => entry.linkSlug && linkBySlug[entry.linkSlug]);
    const clicks = links.reduce((total, link) => total + Object.entries(link.byDay)
        .filter(([day]) => inRange(day, bounds))
        .reduce((linkTotal, [, count]) => linkTotal + toNumber(count), 0), 0);
    return {
        posts: inPeriod.length,
        views,
        likes,
        likeRate: percent(likes, views),
        clicks,
        clickRate: percent(linked.reduce((total, entry) => total + linkBySlug[entry.linkSlug].clicks, 0), sumField(linked, 'views')),
    };
};

const Delta = ({ value, suffix = '%', compareTo }) => {
    if (value == null || Number.isNaN(value)) return <div className="ls-delta">—</div>;
    const direction = value > 0 ? 'is-up' : value < 0 ? 'is-down' : '';
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    return <div className={`ls-delta ${direction}`} title={`Compared with ${compareTo}`}>{sign}{Math.abs(value)}{suffix}</div>;
};

const PlatformSelect = ({ value, onChange, className, ...rest }) => (
    <select className={className} value={value} onChange={onChange} {...rest}>
        {/* Posts logged from the Content Plan can carry a platform this list doesn't have */}
        {value && !PLATFORM_LABELS[value] && <option value={value}>{platformLabel(value)}</option>}
        {PLATFORMS.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
        ))}
    </select>
);

const VideoTypeSelect = ({ value, onChange, className, ...rest }) => (
    <select className={className} value={value || ''} onChange={onChange} {...rest}>
        <option value="">No type</option>
        {value && !VIDEO_TYPES.some(t => t.id === value) && <option value={value}>{value}</option>}
        {VIDEO_TYPES.map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
        ))}
    </select>
);

// Notes grow with their text, so long notes stay readable in the table
const autoResize = (element) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight + 2}px`;
};

const BarChart = ({ bins, metricLabel }) => {
    const peak = Math.max(0, ...bins.map(bin => bin.value));
    const top = niceCeil(peak);
    const labelStep = Math.max(1, Math.ceil(bins.length / 7));
    const unit = metricLabel.toLowerCase();
    return (
        <div
            className="ls-report-chart"
            role="img"
            aria-label={`${metricLabel}: ${bins.map(bin => `${bin.title} ${bin.value.toLocaleString()}`).join(', ')}`}
        >
            <div className="ls-report-chart-axis" aria-hidden="true">
                <span style={{ top: 0 }}>{formatCount(top)}</span>
                <span style={{ top: '50%' }}>{formatCount(top / 2)}</span>
                <span style={{ top: '100%' }}>0</span>
            </div>
            <div className="ls-report-chart-plot" aria-hidden="true">
                {bins.map((bin, index) => {
                    const height = bin.value > 0 ? Math.max(2, (bin.value / top) * 100) : 0;
                    return (
                        <div
                            key={bin.title}
                            className={`ls-report-bar${peak > 0 && bin.value === peak ? ' is-peak' : ''}`}
                            style={{ '--h': `${height}%` }}
                            data-tip={`${bin.title}: ${bin.value.toLocaleString()} ${unit}`}
                        >
                            <div />
                            <span className="ls-report-bar-label">{index % labelStep === 0 ? bin.label : ''}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Mounted with key={entry.id}, so its fields are initialised once per entry
const EditModal = ({ entry, onClose, onSave }) => {
    const [editPlatform, setEditPlatform] = useState(entry.platform);
    const [editVideoType, setEditVideoType] = useState(entry.videoType || '');
    const [editDescription, setEditDescription] = useState(entry.description || '');
    const [editViews, setEditViews] = useState(entry.views ?? '');
    const [editLikes, setEditLikes] = useState(entry.likes ?? '');
    const [editPostDate, setEditPostDate] = useState(entry.postDate || '');
    const [error, setError] = useState('');

    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && onClose();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const handleSave = (event) => {
        event.preventDefault();
        if (!editPostDate) {
            setError('Pick the date this was posted.');
            return;
        }
        if (editViews === '') {
            setError('Add the views for this post (0 is fine).');
            return;
        }
        onSave({
            ...entry,
            platform: editPlatform,
            videoType: editVideoType,
            description: editDescription || `${platformLabel(editPlatform)} ${editVideoType}`.trim(),
            views: toNumber(editViews),
            likes: toNumber(editLikes),
            postDate: editPostDate,
        });
    };

    return (
        <div className="ls-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <form className="ls-modal ls-report-modal" role="dialog" aria-modal="true" aria-labelledby="edit-entry-title" onSubmit={handleSave}>
                <div>
                    <h2 id="edit-entry-title" className="ls-report-modal-title">Edit post</h2>
                    <div className="ls-card-sub">Change what was logged for this post.</div>
                </div>
                <div className="ls-form-grid is-3">
                    <label className="ls-field">
                        <span className="ls-label">Platform</span>
                        <PlatformSelect className="ls-select" value={editPlatform} onChange={(e) => setEditPlatform(e.target.value)} autoFocus />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Type</span>
                        <VideoTypeSelect className="ls-select" value={editVideoType} onChange={(e) => setEditVideoType(e.target.value)} />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Posted on</span>
                        <input
                            className="ls-input"
                            type="date"
                            value={editPostDate}
                            onChange={(e) => {
                                setEditPostDate(e.target.value);
                                setError('');
                            }}
                        />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Views</span>
                        <input
                            className="ls-input"
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={editViews}
                            onChange={(e) => {
                                setEditViews(parseNumberInput(e.target.value));
                                setError('');
                            }}
                            placeholder="0"
                        />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Likes</span>
                        <input
                            className="ls-input"
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={editLikes}
                            onChange={(e) => setEditLikes(parseNumberInput(e.target.value))}
                            placeholder="0"
                        />
                    </label>
                    <div className="ls-field">
                        <span className="ls-label">Like rate</span>
                        <div className="ls-report-readout">{formatPercent(percent(toNumber(editLikes), toNumber(editViews)))}</div>
                    </div>
                    <label className="ls-field is-wide">
                        <span className="ls-label">Notes</span>
                        <textarea
                            className="ls-textarea"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            placeholder="What was the post about?"
                            rows={3}
                        />
                    </label>
                </div>
                {error && <Alert severity="error">{error}</Alert>}
                <div className="ls-report-modal-actions">
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={onClose}>Cancel</button>
                    <button type="submit" className="ls-btn ls-btn-primary">Save changes</button>
                </div>
            </form>
        </div>
    );
};

const ReportScreen = () => {
    const navigate = useNavigate();
    const { currentProduct } = useProducts();
    // Entries live in localStorage; repaired once on load
    const [videoEntries, setVideoEntries] = useState(() => normalizeEntries(loadReportEntries()));
    const [platform, setPlatform] = useState('youtube');
    const [videoType, setVideoType] = useState('');
    const [description, setDescription] = useState('');
    const [views, setViews] = useState('');
    const [likes, setLikes] = useState('');
    const [postDate, setPostDate] = useState(() => formatLocalDate(new Date()));
    const [error, setError] = useState('');
    const [timeRange, setTimeRange] = useState('week');
    const [chartMetric, setChartMetric] = useState('views');
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [filterPlatform, setFilterPlatform] = useState('');
    const [filterVideoType, setFilterVideoType] = useState('');
    const [deleted, setDeleted] = useState(null);
    const [trackedLinks, setTrackedLinks] = useState([]);
    const [linksStatus, setLinksStatus] = useState('loading');
    const viewsInputRef = useRef(null);
    const mountedRef = useRef(true);

    // Clicks come from tracked links (Tracked links page / Content Plan)
    const loadLinks = useCallback(async () => {
        setLinksStatus('loading');
        try {
            const links = await api.listTrackedLinks();
            if (!mountedRef.current) return;
            setTrackedLinks(normalizeLinks(links));
            setLinksStatus('ready');
        } catch (e) {
            if (!mountedRef.current) return;
            setTrackedLinks([]);
            setLinksStatus('error');
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        loadLinks();
        return () => {
            mountedRef.current = false;
        };
    }, [loadLinks]);

    // Update localStorage whenever entries change
    useEffect(() => {
        saveReportEntries(videoEntries);
    }, [videoEntries]);

    // The undo offer for a deleted post goes away after a few seconds
    useEffect(() => {
        if (!deleted) return undefined;
        const timer = window.setTimeout(() => setDeleted(null), 8000);
        return () => window.clearTimeout(timer);
    }, [deleted]);

    const linkBySlug = useMemo(
        () => trackedLinks.reduce((acc, link) => ({ ...acc, [link.slug]: link }), {}),
        [trackedLinks]
    );
    const getEntryClicks = (entry) => (entry.linkSlug && linkBySlug[entry.linkSlug] ? linkBySlug[entry.linkSlug].clicks : null);

    const range = RANGES.find(r => r.id === timeRange) || RANGES[0];
    const metricLabel = chartMetric === 'likes' ? 'Likes' : 'Views';
    const linksLoading = linksStatus === 'loading';

    const bounds = useMemo(() => getRangeBounds(timeRange), [timeRange]);

    const stats = useMemo(() => {
        const current = summarize(videoEntries, trackedLinks, linkBySlug, bounds);
        const previous = summarize(videoEntries, trackedLinks, linkBySlug, getRangeBounds(timeRange, -1));
        return {
            ...current,
            viewsDelta: percentChange(current.views, previous.views),
            likesDelta: percentChange(current.likes, previous.likes),
            clicksDelta: percentChange(current.clicks, previous.clicks),
            likeRateDelta: pointChange(current.likeRate, previous.likeRate),
            clickRateDelta: pointChange(current.clickRate, previous.clickRate),
            totalViews: sumField(videoEntries, 'views'),
        };
    }, [videoEntries, trackedLinks, linkBySlug, bounds, timeRange]);

    const rangeEntries = useMemo(
        () => videoEntries.filter(entry => inRange(entry.postDate, bounds)),
        [videoEntries, bounds]
    );

    const chartBins = useMemo(() => {
        const totals = rangeEntries.reduce((acc, entry) => {
            acc[entry.postDate] = (acc[entry.postDate] || 0) + toNumber(entry[chartMetric]);
            return acc;
        }, {});
        return getChartBins(timeRange, bounds).map(bin => ({
            ...bin,
            value: bin.days.reduce((total, day) => total + (totals[day] || 0), 0),
        }));
    }, [rangeEntries, timeRange, bounds, chartMetric]);
    const chartHasData = chartBins.some(bin => bin.value > 0);

    const platformRows = useMemo(() => {
        const groups = {};
        rangeEntries.forEach(entry => {
            const group = groups[entry.platform] || (groups[entry.platform] = { id: entry.platform, posts: 0, views: 0, likes: 0 });
            group.posts += 1;
            group.views += toNumber(entry.views);
            group.likes += toNumber(entry.likes);
        });
        return Object.values(groups).sort((a, b) => b[chartMetric] - a[chartMetric] || b.posts - a.posts);
    }, [rangeEntries, chartMetric]);
    const platformPeak = Math.max(0, ...platformRows.map(row => row[chartMetric]));

    // Newest first; posts with a broken date go last
    const filteredEntries = useMemo(() => videoEntries
        .filter(entry => (!filterPlatform || entry.platform === filterPlatform) && (!filterVideoType || entry.videoType === filterVideoType))
        .map(entry => ({ entry, time: parseLocalDate(entry.postDate)?.getTime() ?? -Infinity }))
        .sort((a, b) => b.time - a.time)
        .map(({ entry }) => entry), [videoEntries, filterPlatform, filterVideoType]);

    const handleAddEntry = (event) => {
        event.preventDefault();
        if (!postDate) {
            setError('Pick the date this was posted.');
            return;
        }
        if (views === '') {
            setError('Add the views for this post (0 is fine), then press Add post.');
            viewsInputRef.current?.focus();
            return;
        }

        const newEntry = {
            id: Date.now(),
            platform,
            videoType,
            description: description || platformLabel(platform),
            views: toNumber(views),
            likes: toNumber(likes),
            postDate,
        };

        setVideoEntries(entries => [...entries, newEntry]);
        setError('');
        setViews('');
        setLikes('');
        setDescription('');
        const notes = event.currentTarget.querySelector('.ls-report-notes');
        if (notes) notes.style.height = '';
    };

    // Enter in notes adds the post; Shift+Enter starts a new line
    const handleNotesKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.currentTarget.form?.requestSubmit();
        }
    };

    const handleUpdateEntry = (id, field, value) => {
        setVideoEntries(entries => entries.map(entry => (
            entry.id === id ? { ...entry, [field]: value } : entry
        )));
    };

    // A cleared number cell becomes 0 once you leave it
    const handleNumberBlur = (entry, field) => {
        if (entry[field] === '') handleUpdateEntry(entry.id, field, 0);
    };

    const handleDeleteEntry = (id) => {
        const index = videoEntries.findIndex(entry => entry.id === id);
        if (index === -1) return;
        setDeleted({ entry: videoEntries[index], index });
        setVideoEntries(entries => entries.filter(entry => entry.id !== id));
    };

    const handleUndoDelete = () => {
        if (!deleted) return;
        setVideoEntries(entries => {
            const next = [...entries];
            next.splice(Math.min(deleted.index, next.length), 0, deleted.entry);
            return next;
        });
        setDeleted(null);
    };

    const handleSaveEdit = (updatedEntry) => {
        setVideoEntries(entries => entries.map(entry => (
            entry.id === updatedEntry.id ? updatedEntry : entry
        )));
        setSelectedEntry(null);
    };

    const closeModal = useCallback(() => setSelectedEntry(null), []);

    // Clicks arrive with the tracked links; until then a placeholder holds the number's place
    const pending = linksLoading && <span className="ls-skeleton-text" />;
    const metrics = [
        { label: 'Views', value: formatCount(stats.views), delta: stats.viewsDelta },
        { label: 'Link clicks', value: pending || (linksStatus === 'error' ? '—' : formatCount(stats.clicks)), delta: linksLoading ? null : stats.clicksDelta },
        { label: 'Click rate', value: pending || formatPercent(stats.clickRate), delta: linksLoading ? null : stats.clickRateDelta, suffix: ' pts' },
        { label: 'Likes', value: formatCount(stats.likes), delta: stats.likesDelta },
        { label: 'Like rate', value: formatPercent(stats.likeRate), delta: stats.likeRateDelta, suffix: ' pts' },
    ];
    const rangeDates = `${formatDay(formatLocalDate(bounds.start))} – ${formatDay(formatLocalDate(bounds.end))}`;

    return (
        <>
            <div className="ls-page-header">
                <div>
                    <div className="ls-eyebrow">Measure{currentProduct?.name ? ` · ${currentProduct.name}` : ''}</div>
                    <h1 className="ls-page-title">Reports</h1>
                </div>
                <div className="ls-actions">
                    <div className="ls-segmented" role="group" aria-label="Time range">
                        {RANGES.map(option => (
                            <button key={option.id} type="button" aria-pressed={timeRange === option.id} onClick={() => setTimeRange(option.id)}>
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <button type="button" className="ls-btn ls-btn-secondary" onClick={() => navigate('/dashboard/links')}>
                        <Icon name="link" size={18} />Tracked links
                    </button>
                </div>
            </div>

            <div className="ls-card">
                <div className="ls-card-header">
                    <span className="ls-card-title">{range.current}</span>
                    <span className="ls-card-sub">{rangeDates} · change vs {range.previous}</span>
                </div>
                <div className="ls-metrics ls-report-metrics">
                    {metrics.map(metric => (
                        <div key={metric.label}>
                            <div className="ls-metric-label">{metric.label}</div>
                            <div className="ls-metric-value">{metric.value}</div>
                            <Delta value={metric.delta} suffix={metric.suffix} compareTo={range.previous} />
                        </div>
                    ))}
                </div>
                {linksStatus === 'error' && (
                    <Alert
                        severity="error"
                        action={<button type="button" className="ls-btn ls-btn-pill" onClick={loadLinks}>Try again</button>}
                    >
                        Couldn't load your tracked links, so link clicks and click rates are missing. Check that the server is running.
                    </Alert>
                )}
                <div className="ls-card-footer ls-report-footer">
                    All time: {stats.totalViews.toLocaleString()} views across {videoEntries.length} {videoEntries.length === 1 ? 'post' : 'posts'}
                </div>
            </div>

            <div className="ls-grid-12">
                <div className="ls-card ls-span-8">
                    <div className="ls-report-card-head">
                        <div>
                            <div className="ls-card-title">{metricLabel} by {timeRange === '3months' ? 'week' : 'day'}</div>
                            <div className="ls-card-sub">By the date each post went out</div>
                        </div>
                        <div className="ls-segmented" role="group" aria-label="Chart metric">
                            {METRICS.map(option => (
                                <button key={option.id} type="button" aria-pressed={chartMetric === option.id} onClick={() => setChartMetric(option.id)}>
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="ls-report-chart-frame">
                        {chartHasData ? (
                            <BarChart bins={chartBins} metricLabel={metricLabel} />
                        ) : (
                            <div className="ls-placeholder ls-report-chart-empty">
                                <Icon name="bar_chart" />
                                {videoEntries.length > 0
                                    ? `No ${metricLabel.toLowerCase()} logged for posts from ${range.current.toLowerCase()}. Pick a longer range or log a post below.`
                                    : 'Log your first post below and its views show up here by day.'}
                            </div>
                        )}
                    </div>
                </div>

                <div className="ls-card ls-span-4">
                    <div className="ls-card-header">
                        <span className="ls-card-title">By platform</span>
                        <span className="ls-card-sub">{metricLabel}, {range.current.toLowerCase()}</span>
                    </div>
                    {platformRows.length > 0 ? (
                        <div className="ls-report-platforms ls-stagger">
                            {platformRows.map((row, index) => (
                                <div key={row.id} className="ls-report-platform" style={{ '--i': index }}>
                                    <div className="ls-report-platform-head">
                                        <div style={{ minWidth: 0 }}>
                                            <div className="ls-row-title">{platformLabel(row.id)}</div>
                                            <div className="ls-row-sub">
                                                {row.posts} {row.posts === 1 ? 'post' : 'posts'} · {formatPercent(percent(row.likes, row.views))} like rate
                                            </div>
                                        </div>
                                        <div className="ls-report-platform-value">{row[chartMetric].toLocaleString()}</div>
                                    </div>
                                    <div className="ls-report-track">
                                        <div style={{ width: `${platformPeak ? Math.max(2, (row[chartMetric] / platformPeak) * 100) : 0}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="ls-empty">
                            Each platform you post on shows up here with its {metricLabel.toLowerCase()} and like rate, once you log posts from {range.current.toLowerCase()}.
                        </div>
                    )}
                </div>
            </div>

            <div className="ls-card">
                <div className="ls-report-card-head">
                    <div>
                        <div className="ls-card-title">Posts</div>
                        <div className="ls-card-sub">
                            {videoEntries.length
                                ? `${videoEntries.length} logged. Edit any cell to update it.`
                                : 'Log views and likes after a post has been up for a day or two.'}
                        </div>
                    </div>
                    {videoEntries.length > 0 && (
                        <div className="ls-report-filters">
                            <select className="ls-select is-compact" aria-label="Filter by platform" onChange={(e) => setFilterPlatform(e.target.value)} value={filterPlatform}>
                                <option value="">All platforms</option>
                                {PLATFORMS.map(p => (
                                    <option key={p.id} value={p.id}>{p.label}</option>
                                ))}
                            </select>
                            <select className="ls-select is-compact" aria-label="Filter by type" onChange={(e) => setFilterVideoType(e.target.value)} value={filterVideoType}>
                                <option value="">All types</option>
                                {VIDEO_TYPES.map(t => (
                                    <option key={t.id} value={t.id}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <form className="ls-report-add" onSubmit={handleAddEntry} aria-label="Log a post">
                    <label className="ls-field">
                        <span className="ls-label">Posted on</span>
                        <input
                            className="ls-input is-compact"
                            type="date"
                            value={postDate}
                            onChange={(e) => {
                                setPostDate(e.target.value);
                                setError('');
                            }}
                        />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Platform</span>
                        <PlatformSelect className="ls-select is-compact" value={platform} onChange={(e) => setPlatform(e.target.value)} />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Type</span>
                        <VideoTypeSelect className="ls-select is-compact" value={videoType} onChange={(e) => setVideoType(e.target.value)} />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Views</span>
                        <input
                            ref={viewsInputRef}
                            className="ls-input is-compact"
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={views}
                            onChange={(e) => {
                                setViews(parseNumberInput(e.target.value));
                                setError('');
                            }}
                            placeholder="0"
                        />
                    </label>
                    <label className="ls-field">
                        <span className="ls-label">Likes</span>
                        <input
                            className="ls-input is-compact"
                            type="number"
                            min="0"
                            inputMode="numeric"
                            value={likes}
                            onChange={(e) => setLikes(parseNumberInput(e.target.value))}
                            placeholder="0"
                        />
                    </label>
                    <label className="ls-field ls-report-add-notes">
                        <span className="ls-label">Notes</span>
                        <textarea
                            ref={autoResize}
                            rows={1}
                            className="ls-textarea is-compact ls-report-notes"
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                autoResize(e.target);
                            }}
                            onKeyDown={handleNotesKeyDown}
                            placeholder="What was the post about?"
                        />
                    </label>
                    <button type="submit" className="ls-btn ls-btn-primary ls-report-add-button">
                        <Icon name="add" size={18} />Add post
                    </button>
                </form>
                {error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <div className="ls-hint">
                        Enter adds the post (Shift+Enter for a new line in notes).{views !== '' && toNumber(views) > 0 ? ` Like rate: ${formatPercent(percent(toNumber(likes), toNumber(views)))}.` : ''}
                    </div>
                )}

                {deleted && (
                    <div className="ls-notice ls-report-undo" role="status">
                        <Icon name="delete" size={18} />
                        <span>Deleted “{deleted.entry.description || 'Untitled post'}”.</span>
                        <button type="button" className="ls-link" onClick={handleUndoDelete}>Undo</button>
                    </div>
                )}

                {filteredEntries.length > 0 ? (
                    <div className="ls-table-wrap ls-report-table-wrap">
                        <table className="ls-table ls-report-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Platform</th>
                                    <th>Type</th>
                                    <th>Notes</th>
                                    <th className="is-num">Views</th>
                                    <th className="is-num">Likes</th>
                                    <th className="is-num">Like rate</th>
                                    <th className="is-num">Link clicks</th>
                                    <th className="is-num">Click rate</th>
                                    <th><span className="ls-report-sr">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody className="ls-stagger">
                                {filteredEntries.map((entry, index) => {
                                    const clicks = getEntryClicks(entry);
                                    const name = entry.description || 'this post';
                                    return (
                                        <tr key={entry.id} style={{ '--i': index }}>
                                            <td>
                                                <input
                                                    type="date"
                                                    className="ls-input is-compact ls-report-cell"
                                                    aria-label={`Date for ${name}`}
                                                    value={entry.postDate}
                                                    onChange={(e) => handleUpdateEntry(entry.id, 'postDate', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <PlatformSelect
                                                    className="ls-select is-compact ls-report-cell"
                                                    aria-label={`Platform for ${name}`}
                                                    value={entry.platform}
                                                    onChange={(e) => handleUpdateEntry(entry.id, 'platform', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <VideoTypeSelect
                                                    className="ls-select is-compact ls-report-cell"
                                                    aria-label={`Type for ${name}`}
                                                    value={entry.videoType}
                                                    onChange={(e) => handleUpdateEntry(entry.id, 'videoType', e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <textarea
                                                    ref={autoResize}
                                                    rows={1}
                                                    className="ls-textarea is-compact ls-report-cell ls-report-notes"
                                                    aria-label="Notes"
                                                    value={entry.description}
                                                    placeholder="Add notes"
                                                    onChange={(e) => {
                                                        handleUpdateEntry(entry.id, 'description', e.target.value);
                                                        autoResize(e.target);
                                                    }}
                                                />
                                            </td>
                                            <td className="is-num">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    inputMode="numeric"
                                                    className="ls-input is-compact ls-report-cell is-num"
                                                    aria-label={`Views for ${name}`}
                                                    value={entry.views ?? ''}
                                                    placeholder="0"
                                                    onChange={(e) => handleUpdateEntry(entry.id, 'views', parseNumberInput(e.target.value))}
                                                    onBlur={() => handleNumberBlur(entry, 'views')}
                                                />
                                            </td>
                                            <td className="is-num">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    inputMode="numeric"
                                                    className="ls-input is-compact ls-report-cell is-num"
                                                    aria-label={`Likes for ${name}`}
                                                    value={entry.likes ?? ''}
                                                    placeholder="0"
                                                    onChange={(e) => handleUpdateEntry(entry.id, 'likes', parseNumberInput(e.target.value))}
                                                    onBlur={() => handleNumberBlur(entry, 'likes')}
                                                />
                                            </td>
                                            <td className="is-num">{formatPercent(percent(toNumber(entry.likes), toNumber(entry.views)))}</td>
                                            <td className="is-num">
                                                {clicks != null ? (
                                                    clicks.toLocaleString()
                                                ) : linksLoading && entry.linkSlug ? (
                                                    <span className="ls-skeleton-text" />
                                                ) : (
                                                    // Attach a tracked link to see this post's clicks
                                                    <select
                                                        className="ls-select is-compact ls-report-cell ls-report-link-select"
                                                        value=""
                                                        onChange={(e) => handleUpdateEntry(entry.id, 'linkSlug', e.target.value)}
                                                        aria-label={`Attach a tracked link to ${name}`}
                                                        disabled={linksLoading}
                                                    >
                                                        <option value="">+ Link</option>
                                                        {trackedLinks.length === 0 && <option value="" disabled>No tracked links yet</option>}
                                                        {trackedLinks.map(link => (
                                                            <option key={link.slug} value={link.slug}>
                                                                {link.campaign || link.slug}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </td>
                                            <td className="is-num">{formatPercent(clicks == null ? null : percent(clicks, toNumber(entry.views)))}</td>
                                            <td>
                                                <div className="ls-report-row-actions">
                                                    <button type="button" className="ls-btn ls-btn-icon" aria-label={`Edit ${name}`} title="Edit" onClick={() => setSelectedEntry(entry)}>
                                                        <Icon name="edit" size={18} />
                                                    </button>
                                                    <button type="button" className="ls-btn ls-btn-icon" aria-label={`Delete ${name}`} title="Delete" onClick={() => handleDeleteEntry(entry.id)}>
                                                        <Icon name="delete" size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : videoEntries.length > 0 ? (
                    <div className="ls-placeholder ls-report-table-empty">
                        <Icon name="filter_alt_off" />
                        No posts match these filters.
                        <button
                            type="button"
                            className="ls-link"
                            onClick={() => {
                                setFilterPlatform('');
                                setFilterVideoType('');
                            }}
                        >
                            Clear filters
                        </button>
                    </div>
                ) : (
                    <div className="ls-placeholder ls-report-table-empty">
                        <Icon name="table_chart" />
                        <span>No posts logged yet. Add one above, or mark posts as posted in your content plan and log their results there.</span>
                        <button type="button" className="ls-link" onClick={() => navigate('/dashboard/plan')}>
                            Open content plan<Icon name="arrow_forward" size={16} />
                        </button>
                    </div>
                )}
                {videoEntries.length > 0 && linksStatus === 'ready' && trackedLinks.length === 0 && (
                    <div className="ls-hint">
                        Link clicks come from tracked links.{' '}
                        <button type="button" className="ls-link" onClick={() => navigate('/dashboard/links')}>Create a tracked link</button>
                        {' '}and attach it to a post to see its click rate.
                    </div>
                )}
            </div>

            {selectedEntry && (
                <EditModal
                    key={selectedEntry.id}
                    entry={selectedEntry}
                    onClose={closeModal}
                    onSave={handleSaveEdit}
                />
            )}
        </>
    );
};

export default ReportScreen;
